/**
 * Where a given character sits inside a textarea, in pixels relative to the
 * textarea's own box. A textarea exposes no such API, so the text up to that
 * index is laid out again in an off-screen div that copies the textarea's
 * metrics; the marker span at the end of it lands where the character does.
 */

/** Everything that can move a glyph: font, spacing, padding, borders, wrapping. */
const MIRRORED_PROPERTIES = [
  "boxSizing",
  "width",
  "borderTopWidth",
  "borderRightWidth",
  "borderBottomWidth",
  "borderLeftWidth",
  "paddingTop",
  "paddingRight",
  "paddingBottom",
  "paddingLeft",
  "fontStyle",
  "fontVariant",
  "fontWeight",
  "fontStretch",
  "fontSize",
  "fontFamily",
  "lineHeight",
  "letterSpacing",
  "wordSpacing",
  "textIndent",
  "textTransform",
  "whiteSpace",
  "wordBreak",
  "overflowWrap",
  "tabSize",
] as const;

export type CaretPosition = {
  /** Offset of the character's left edge from the textarea's left edge. */
  left: number;
  /** Offset of the top of its line from the textarea's top edge. */
  top: number;
  /** Height of that line, so a popover can be placed under it rather than over it. */
  lineHeight: number;
};

export const caretCoordinates = (textarea: HTMLTextAreaElement, index: number): CaretPosition => {
  const computed = window.getComputedStyle(textarea);
  const mirror = document.createElement("div");

  for (const property of MIRRORED_PROPERTIES) mirror.style[property] = computed[property];
  // A textarea wraps and never collapses trailing spaces; the div must do both.
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.overflowWrap = "break-word";
  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.top = "0";
  mirror.style.left = "0";
  mirror.textContent = textarea.value.slice(0, index);

  const marker = document.createElement("span");
  // Non-empty, so the span has a box even at the very start of a line.
  marker.textContent = "​";
  mirror.appendChild(marker);

  document.body.appendChild(mirror);
  const lineHeight = marker.offsetHeight || parseFloat(computed.lineHeight) || 0;
  // offsetLeft/offsetTop are measured from inside the mirror's border, so the
  // textarea's own borders have to be added back on.
  const position: CaretPosition = {
    left: marker.offsetLeft + parseFloat(computed.borderLeftWidth) - textarea.scrollLeft,
    top: marker.offsetTop + parseFloat(computed.borderTopWidth) - textarea.scrollTop,
    lineHeight,
  };
  document.body.removeChild(mirror);

  return position;
};
